import * as React from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  redirect,
  Link,
  useNavigate,
} from '@tanstack/react-router';
import { z } from 'zod';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { UserPlusIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { adminUsersOptions } from '@/hooks/query-options';
import {
  createUserColumns,
  type SuperAdminUser,
} from '@/components/super-admin/columns';
import { CreateUserDialog } from '@/components/super-admin/create-user-dialog';

export const USERS_PAGE_SIZE = 20;

// Pagination + search state lives in the URL: shareable, back/forward
// works, and the loader can warm the query cache before render. Combined
// with `defaultPreload: 'intent'` on the router, hovering the Previous /
// Next links runs the loader → ensureQueryData → cache warm. By the time
// the click lands, useSuspenseQuery in the component returns instantly.
export const Route = createFileRoute('/_auth/_side-layout/super-admin/users')({
  component: SuperAdminUsers,
  beforeLoad: async ({ context }) => {
    if (context.platformRole !== 'admin') {
      throw redirect({ to: '/' });
    }
  },
  validateSearch: z.object({
    offset: z.number().int().nonnegative().catch(0),
    q: z.string().catch(''),
  }),
  loaderDeps: ({ search: { offset, q } }) => ({ offset, q }),
  loader: ({ deps: { offset, q }, context: { queryClient } }) =>
    queryClient.ensureQueryData(
      adminUsersOptions({ offset, search: q, limit: USERS_PAGE_SIZE }),
    ),
});

function SuperAdminUsers() {
  const [createOpen, setCreateOpen] = React.useState(false);
  const { offset, q } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Search input is local state, debounced into the URL. Each navigate
  // updates `q` in the URL → loader re-runs → ensureQueryData fetches →
  // useSuspenseQuery picks up the new data. Reset to offset 0 on a new
  // query so we don't land past the end.
  const [searchInput, setSearchInput] = React.useState(q);
  React.useEffect(() => {
    if (searchInput === q) return;
    const timer = setTimeout(() => {
      navigate({
        to: '/super-admin/users',
        search: { q: searchInput, offset: 0 },
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, navigate]);

  // useSuspenseQuery is safe here because the route loader called
  // ensureQueryData with the same options before this component rendered.
  // adminUsersOptions also has `placeholderData: (prev) => prev` so
  // navigation between pages reuses the previous result while the new
  // page resolves — no Suspense flash in the common case.
  const { data } = useSuspenseQuery(
    adminUsersOptions({ offset, search: q, limit: USERS_PAGE_SIZE }),
  );
  const users = data.users;
  const total = data.total;

  const refresh = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  }, [queryClient]);

  const columns = React.useMemo<ColumnDef<SuperAdminUser>[]>(
    () => createUserColumns(refresh),
    [refresh],
  );

  const currentPage = Math.floor(offset / USERS_PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / USERS_PAGE_SIZE);
  const canGoPrev = offset > 0;
  const canGoNext = offset + USERS_PAGE_SIZE < total;

  return (
    <>
      <PageHeader
        title="Users"
        description="Manage all platform users."
        actions={
          <Button onClick={() => setCreateOpen(true)} iconLeft={<UserPlusIcon />}>
            Create User
          </Button>
        }
      />
      <PageContent>
        <div className="flex items-center gap-4">
          <div className="relative max-w-sm flex-1">
            <MagnifyingGlassIcon className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              placeholder="Search by email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          {total > 0 && (
            <span className="text-muted-foreground text-sm">
              {total} user{total !== 1 && 's'}
            </span>
          )}
        </div>

        <DataTable
          columns={columns}
          data={users}
          manualPagination
          emptyMessage="No Users Found"
        />

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              {/*
                Pagination via <Link> + the router's defaultPreload='intent':
                hover/focus on Previous or Next runs the route loader, which
                calls ensureQueryData on the adjacent page. By the time the
                user clicks, the data is already cached. No manual prefetch
                or onMouseEnter plumbing.
              */}
              {canGoPrev ? (
                <Button asChild variant="outline" size="sm">
                  <Link
                    from="/super-admin/users"
                    search={(prev) => ({
                      ...prev,
                      offset: Math.max(0, prev.offset - USERS_PAGE_SIZE),
                    })}
                  >
                    Previous
                  </Link>
                </Button>
              ) : (
                <Button variant="outline" size="sm" disabled>
                  Previous
                </Button>
              )}
              {canGoNext ? (
                <Button asChild variant="outline" size="sm">
                  <Link
                    from="/super-admin/users"
                    search={(prev) => ({
                      ...prev,
                      offset: prev.offset + USERS_PAGE_SIZE,
                    })}
                  >
                    Next
                  </Link>
                </Button>
              ) : (
                <Button variant="outline" size="sm" disabled>
                  Next
                </Button>
              )}
            </div>
          </div>
        )}

        <CreateUserDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={refresh}
        />
      </PageContent>
    </>
  );
}
